import {
  type CheckOperator,
  type CheckRule,
  type CheckRuleCondition,
} from '@internal/backstage-plugin-pulse-common';

/** Editable leaf: a single rule condition. */
export type ConditionNode = {
  kind: 'condition';
  key: string;
  factRef: string;
  path: string;
  operator: CheckOperator;
  value: string;
};

/** Editable branch: an `all`/`any` group of conditions and/or nested groups. */
export type GroupNode = {
  kind: 'group';
  key: string;
  combinator: 'all' | 'any';
  children: RuleNode[];
};

export type RuleNode = ConditionNode | GroupNode;

let nodeKeySeq = 0;
const nextKey = (prefix: string) => {
  nodeKeySeq += 1;
  return `${prefix}-${nodeKeySeq}`;
};

const formatConditionValue = (value: unknown): string => {
  if (value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
};

export const createConditionNode = (
  partial?: Partial<Omit<ConditionNode, 'kind' | 'key'>>,
): ConditionNode => ({
  kind: 'condition',
  key: nextKey('condition'),
  factRef: partial?.factRef ?? '',
  path: partial?.path ?? '',
  operator: partial?.operator ?? 'equals',
  value: partial?.value ?? '',
});

export const createGroupNode = (
  combinator: 'all' | 'any',
  children: RuleNode[] = [],
): GroupNode => ({
  kind: 'group',
  key: nextKey('group'),
  combinator,
  children,
});

/** Counts the conditions in a group, including those inside nested groups. */
export const countConditions = (node: GroupNode): number =>
  node.children.reduce(
    (total, child) =>
      total + (child.kind === 'condition' ? 1 : countConditions(child)),
    0,
  );

const isRuleCondition = (
  item: CheckRuleCondition | CheckRule,
): item is CheckRuleCondition =>
  'factRef' in item && 'path' in item && 'operator' in item;

/** Parses a single `CheckRule` node into a `GroupNode`. A node that sets both
 * `all` and `any` is represented as an ALL group wrapping an `all` subgroup and
 * an `any` subgroup, which is semantically identical. */
const ruleToGroup = (rule: CheckRule): GroupNode => {
  const toNode = (item: CheckRuleCondition | CheckRule): RuleNode =>
    isRuleCondition(item)
      ? createConditionNode({
          factRef: item.factRef,
          path: item.path,
          operator: item.operator,
          value: formatConditionValue(item.value),
        })
      : ruleToGroup(item);

  const allItems = rule.all ?? [];
  const anyItems = rule.any ?? [];

  if (allItems.length > 0 && anyItems.length > 0) {
    return createGroupNode('all', [
      createGroupNode('all', allItems.map(toNode)),
      createGroupNode('any', anyItems.map(toNode)),
    ]);
  }
  if (anyItems.length > 0) {
    return createGroupNode('any', anyItems.map(toNode));
  }
  return createGroupNode('all', allItems.map(toNode));
};

/** Builds the editable tree for a check's rule, always rooted at a group with
 * at least one condition so the form has something to render. */
export const ruleToTree = (rule?: CheckRule): GroupNode => {
  const root = rule ? ruleToGroup(rule) : createGroupNode('all');
  if (root.children.length === 0) {
    root.children.push(createConditionNode());
  }
  return root;
};

const isCompleteCondition = (node: ConditionNode): boolean =>
  node.factRef.trim().length > 0 && node.path.trim().length > 0;

const conditionToRule = (node: ConditionNode): CheckRuleCondition => ({
  factRef: node.factRef.trim(),
  path: node.path.trim(),
  operator: node.operator,
  ...(node.value.trim() ? { value: node.value } : {}),
});

const serializeNode = (
  node: RuleNode,
): CheckRuleCondition | CheckRule | undefined => {
  if (node.kind === 'condition') {
    return isCompleteCondition(node) ? conditionToRule(node) : undefined;
  }
  const items = node.children
    .map(serializeNode)
    .filter((item): item is CheckRuleCondition | CheckRule => Boolean(item));
  if (items.length === 0) {
    return undefined;
  }
  return { [node.combinator]: items };
};

/** Serializes the editable tree back into a `CheckRule`. Incomplete conditions
 * and empty groups are dropped; a root of only conditions stays flat (e.g.
 * `{ all: [...] }`) so simple checks round-trip unchanged. */
export const treeToRule = (root: GroupNode): CheckRule => {
  const serialized = serializeNode(root);
  if (serialized && !isRuleCondition(serialized)) {
    return serialized;
  }
  return { [root.combinator]: [] };
};

/** Whether the tree has at least one fully-filled condition. */
export const treeHasCompleteCondition = (node: RuleNode): boolean => {
  if (node.kind === 'condition') {
    return isCompleteCondition(node);
  }
  return node.children.some(treeHasCompleteCondition);
};

/** All non-empty fact refs referenced anywhere in the tree. */
export const collectConditionRefs = (node: RuleNode): string[] => {
  if (node.kind === 'condition') {
    return node.factRef.trim() ? [node.factRef.trim()] : [];
  }
  return node.children.flatMap(collectConditionRefs);
};
